import { useEffect, useState } from 'react';
import { vec3 } from 'gl-matrix';
import { useRecoilState, useRecoilValue } from 'recoil';
import { environmentState, mainViewState } from '~/state/atoms/engineAtoms';
import {
    currenPositionAutoScrollState,
    viewportOrientationOrderState,
} from '~/state/atoms/viewerAtoms';
import { Orientations } from '~/engine/view/views/PlaneView';
/**
 * useSlider Hook
 *
 * A hook to manage slider functionality for navigating slices in a 3D view based on orientation.
 *
 * @param structure - The ROI number of the structure to scroll through.
 * @returns - Slider value, change handler, and related state.
 */
export const useSlider = (structure: number) => {
    const [currentPosition, setCurrentPosition] = useRecoilState(
        currenPositionAutoScrollState
    );
    const environment = useRecoilValue(environmentState);
    const viewportOrientationOrder = useRecoilValue(
        viewportOrientationOrderState
    );

    const mainView = useRecoilValue(mainViewState);

    const [sliderValue, setSliderValue] = useState<number>(0);
    const [minPosition, setMinPosition] = useState<number>(0);
    const [maxPosition, setMaxPosition] = useState<number>(100);
    const [currentOrientation, setCurrentOrientation] =
        useState<Orientations | null>(null);

    useEffect(() => {
        if (
            !environment ||
            !mainView ||
            viewportOrientationOrder.length === 0
        ) {
            return;
        }
        const roi = environment?.structureSet?.getRoi(structure);

        if (!roi || roi.isEmpty || !roi.texture) {
            return;
        }
        const { min, max, center } = roi.texture.contentBoundingBox;
        if (!min || !max || !center) {
            console.warn('No valid bounding box found for the structure.');
            return;
        }
        const viewDirection = mainView.viewDirection;
        const firstOrientation = viewportOrientationOrder[0];
        const startingPosition = vec3.add(
            vec3.create(),
            center,
            vec3.scale(
                vec3.create(),
                viewDirection,
                vec3.dot(
                    viewDirection,
                    vec3.subtract(vec3.create(), min, center)
                )
            )
        );
        // Update min and max positions based on the orientation
        switch (firstOrientation) {
            case Orientations.SAGITTAL_ORIENTATION:
                setMinPosition(min[0] || 0);
                setMaxPosition(max[0] || 100);
                setSliderValue(center[0]);
                break;
            case Orientations.CORONAL_ORIENTATION:
                setMinPosition(min[1] || 0);
                setMaxPosition(max[1] || 100);
                setSliderValue(center[1]);
                break;
            case Orientations.TRANSVERSAL_ORIENTATION:
                setMinPosition(min[2] ?? 0);
                setMaxPosition(max[2] ?? 100);
                setSliderValue(center[2]);
                break;
            default:
                console.warn('Unknown orientation');
                break;
        }

        setCurrentPosition(vec3.clone(startingPosition));
        setCurrentOrientation(firstOrientation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        structure,
        environment,
        mainView,
        viewportOrientationOrder,
        minPosition,
    ]);

    const handleChange = (_event: unknown, newValue: number | number[]) => {
        if (!mainView || !currentPosition) {
            return;
        }

        const newSliderValue = Array.isArray(newValue) ? newValue[0] : newValue;
        const newPosition = vec3.clone(currentPosition);

        switch (currentOrientation) {
            case Orientations.SAGITTAL_ORIENTATION:
                newPosition[0] = newSliderValue;
                break;
            case Orientations.CORONAL_ORIENTATION:
                newPosition[1] = newSliderValue;
                break;
            case Orientations.TRANSVERSAL_ORIENTATION:
                newPosition[2] = newSliderValue;
                break;
            default:
                console.warn('Unknown orientation');
                break;
        }

        mainView.updatePlaneIntersectionPoint(newPosition);
        setSliderValue(newSliderValue);
    };

    return {
        sliderValue,
        handleChange,
        minPosition,
        maxPosition,
        currentOrientation,
    };
};
